/**
 * QMS Ticket Links Component
 * Manages related/linked tickets with search and relationship types
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link2, X, Plus, Search, AlertCircle, ExternalLink } from 'lucide-react';
import { useLinkQmsTicket, useUnlinkQmsTicket, useSearchLinkableTickets } from '../../hooks/useQmsTicket';
import { useSession } from '@/hooks/useSession';
import { getQmsOrganisationId } from '../../utils/organisation';
import type { QmsTicket, QmsRelationshipType } from '../../types/qms.types';
import { extractId } from '../../types/qms.types';

interface QmsTicketLinksProps {
  ticket: QmsTicket;
  onLinksUpdated?: () => void;
}

export function QmsTicketLinks({ ticket, onLinksUpdated }: QmsTicketLinksProps) {
  const { t } = useTranslation('qms');
  const { user } = useSession();
  const qmsOrganisation = getQmsOrganisationId(user?.organisation);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkMutation = useLinkQmsTicket();
  const unlinkMutation = useUnlinkQmsTicket();

  // Navigate to a related ticket
  const handleNavigateToTicket = (ticketId: string) => {
    const params = searchParams.toString();
    navigate(`/qms/${ticketId}${params ? `?${params}` : ''}`);
  };

  const [isAdding, setIsAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRelationship, setSelectedRelationship] = useState<QmsRelationshipType>('related_to');
  const [error, setError] = useState<string | null>(null);

  const relatedTickets = useMemo(() => ticket.related_tickets || [], [ticket.related_tickets]);

  // Get IDs of already linked tickets to exclude from search
  const excludeLinkedIds = useMemo(() => {
    return relatedTickets
      .map((rt) => extractId(rt.ticket))
      .filter((id): id is string => !!id);
  }, [relatedTickets]);

  // Search query for linkable tickets
  const { data: searchResults = [], isLoading: isSearching } = useSearchLinkableTickets(
    qmsOrganisation,
    searchTerm,
    ticket._id,
    excludeLinkedIds
  );

  const relationshipOptions: Array<{ value: QmsRelationshipType; label: string }> = [
    { value: 'related_to', label: t('links.relationships.related_to') },
    { value: 'duplicate_of', label: t('links.relationships.duplicate_of') },
    { value: 'caused_by', label: t('links.relationships.caused_by') },
    { value: 'blocks', label: t('links.relationships.blocks') },
    { value: 'parent_of', label: t('links.relationships.parent_of') },
    { value: 'child_of', label: t('links.relationships.child_of') },
  ];

  const handleLink = async (targetTicket: QmsTicket) => {
    setError(null);

    try {
      await linkMutation.mutateAsync({
        ticketId: ticket._id,
        relatedTicketId: targetTicket._id,
        relationship: selectedRelationship,
      });
      setSearchTerm('');
      setIsAdding(false);
      onLinksUpdated?.();
    } catch (err: any) {
      setError(err?.message || t('links.errors.linkFailed'));
    }
  };

  const handleUnlink = async (relatedTicketId: string) => {
    if (!confirm(t('links.confirmations.unlink'))) return;

    setError(null);

    try {
      await unlinkMutation.mutateAsync({
        ticketId: ticket._id,
        relatedTicketId,
      });
      onLinksUpdated?.();
    } catch (err: any) {
      setError(err?.message || t('links.errors.unlinkFailed'));
    }
  };

  const getRelationshipLabel = (relationship: QmsRelationshipType): string => {
    const option = relationshipOptions.find((o) => o.value === relationship);
    return option ? option.label : relationship;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Link2 className="h-5 w-5" />
          {t('links.title')}
          {relatedTickets.length > 0 && (
            <span className="text-sm font-normal text-gray-500">({relatedTickets.length})</span>
          )}
        </h3>

        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-atek-blue hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t('links.addLink')}
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 text-sm text-red-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Add link form */}
      {isAdding && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          {/* Relationship selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('links.relationshipType')}
            </label>
            <select
              value={selectedRelationship}
              onChange={(e) => setSelectedRelationship(e.target.value as QmsRelationshipType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent"
            >
              {relationshipOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>          </div>

          {/* Search input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('links.searchPlaceholder')}
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('links.searchHint')}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Search results */}
          {searchTerm.length >= 2 && (
            <div className="max-h-60 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-2 bg-white">
              {isSearching ? (
                <div className="text-center py-4 text-gray-500">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-atek-blue mx-auto"></div>
                  <p className="mt-2 text-sm">{t('links.searching')}</p>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  <p className="text-sm">{t('links.noResults')}</p>
                </div>
              ) : (
                searchResults.map((result) => (
                  <button
                    key={result._id}
                    onClick={() => handleLink(result)}
                    disabled={linkMutation.isPending}
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-atek-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-2">
                      {result.ticket_id && (
                        <span className="text-sm font-semibold text-atek-blue">{result.ticket_id}</span>
                      )}
                      <p className="font-medium text-gray-900 truncate">{result.title}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                        {result.type === 'capa' ? 'CAPA' : 'Ticket'}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                        {result.status}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setIsAdding(false);
                setSearchTerm('');
                setError(null);
              }}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              {t('links.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Related tickets list */}
      {relatedTickets.length === 0 ? (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
          <Link2 className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p>{t('links.noLinks')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {relatedTickets.map((relatedTicket) => {
            const ticketId = extractId(relatedTicket.ticket);
            const isPopulated = typeof relatedTicket.ticket === 'object' && relatedTicket.ticket !== null;

            return (
              <div
                key={ticketId}
                className="flex items-start justify-between bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow"
              >
                <button
                  onClick={() => ticketId && handleNavigateToTicket(ticketId)}
                  className="flex-1 min-w-0 text-left hover:bg-gray-50 -m-2 p-2 rounded-lg transition-colors"
                >
                  {isPopulated && typeof relatedTicket.ticket !== 'string' ? (
                    <>
                      <div className="flex items-center gap-2">
                        {relatedTicket.ticket.ticket_id && (
                          <span className="text-sm font-semibold text-atek-blue">{relatedTicket.ticket.ticket_id}</span>
                        )}
                        <p className="font-medium text-gray-900 truncate hover:text-atek-blue flex items-center gap-1">
                          {relatedTicket.ticket.title}
                          <ExternalLink className="h-3 w-3 text-gray-400" />
                        </p>
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                          {getRelationshipLabel(relatedTicket.relationship)}
                        </span>
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                          {relatedTicket.ticket.type === 'capa' ? 'CAPA' : 'Ticket'}
                        </span>
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                          {relatedTicket.ticket.status}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 hover:text-atek-blue flex items-center gap-1">
                        Ticket ID: {ticketId}
                        <ExternalLink className="h-3 w-3 text-gray-400" />
                      </p>
                      <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded inline-block mt-1">
                        {getRelationshipLabel(relatedTicket.relationship)}
                      </span>
                    </>
                  )}
                </button>

                <button
                  onClick={() => ticketId && handleUnlink(ticketId)}
                  disabled={unlinkMutation.isPending}
                  className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                  title={t('links.unlink')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
